/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import ApiService from '../../../services/ApiService';
import {
    TextField, MenuItem, Button, Grid, Typography, Box, Paper,
    CircularProgress, Alert, FormHelperText, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions,
    FormControl, InputLabel, Select, Checkbox, FormControlLabel, Switch,
    Accordion, AccordionSummary, AccordionDetails, Autocomplete,
    Table, TableBody, TableCell, TableContainer, TableRow, TableHead,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';

// Desired Installation Date must be beyond 90 days from today (earliest selectable: day 91).
const MIN_INSTALL_DAYS = 90;
const MIN_INSTALL_DATE = (() => {
    const d = new Date();
    d.setDate(d.getDate() + MIN_INSTALL_DAYS + 1);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; // local YYYY-MM-DD
})();
const INSTALL_DATE_MSG = `Date must be beyond ${MIN_INSTALL_DAYS} days from today (on or after ${MIN_INSTALL_DATE})`;

// Same labeling convention as Circuit Ordering's cost breakdown table.
const COST_BREAKDOWN_LABELS = {
    sdWanCircuits: 'SD-WAN Circuit(s) (908138)',
    sdWanManagement: 'SD-WAN Management (908134)',
    sdWanRouter: 'SD-WAN Router (908137)',
    sdWanImacd: 'SD-WAN IMACD — OTC Provider (908374)',
    accentureEffort: 'Integrator Effort — Accenture (905513)',
    sdWanEffort: 'SD-WAN Effort (2 PD) (908374)',
    odmTeamEffort: 'ODM Team Effort (905513)',
    wopBase: 'WOP Base (Accenture)',
    wopRejectionFee: 'WOP Rejection Fee',
    aztPmoEffort: 'AzT PM Effort',
    aztSdWanEffort: 'AzT SD-WAN Effort (908374)',
    lanPortMrc: 'LAN Ports (MRC)',
    wifiApMrc: 'WiFi Access Points (MRC)',
};

// Pre-quote these are always 0 (real values only land once Accenture's WOP is parsed) —
// hidden from the estimate breakdown so they don't read as a priced line item.
const HIDDEN_BREAKDOWN_KEYS = ['wopBase', 'wopRejectionFee', 'wopOverhead', 'wopMarkup', 'wopInterest'];

// MRC (monthly recurring) vs NRC (one-time) per breakdown component, for the Type column.
const COST_BREAKDOWN_TYPES = {
    sdWanCircuits: 'MRC',
    sdWanManagement: 'MRC',
    sdWanRouter: 'MRC',
    sdWanImacd: 'NRC',
    accentureEffort: 'NRC',
    sdWanEffort: 'NRC',
    odmTeamEffort: 'NRC',
    wopBase: 'NRC',
    wopRejectionFee: 'NRC',
    aztPmoEffort: 'NRC',
    aztSdWanEffort: 'NRC',
    lanPortMrc: 'MRC',
    wifiApMrc: 'MRC',
};

const SITE_TYPE_OPTIONS = ['Normal Site', 'Call Center', 'Data Center'];
const YES_NO = ['Yes', 'No'];
const EXPEDITE_OPTIONS = ['BAU', 'Expedite'];
const DDI_SOLUTION_OPTIONS = ['PRIs', 'SIP trunk'];
const CONNECTION_TYPE_OPTIONS = ['Copper', 'Fiber'];
const WIFI_DATA_USAGE_OPTIONS = ['Basic Data', 'Heavy Data', 'Voice', 'Video', 'Mixed'];
const SERVICES = [
    { key: 'WAN', desc: 'SD-WAN circuits, bandwidth' },
    { key: 'LAN', desc: 'Access switches, ports' },
    { key: 'WiFi', desc: 'Access points, survey' },
    { key: 'Voice', desc: 'Phones, DDI, IVR' },
];
// Illustrative list — real OE directory integration is a follow-up.
const ALL_OES = [
    'Allianz Netherlands', 'Allianz Technology', 'Allianz Global Corporate & Specialty', 'Allianz Germany',
    'Allianz France', 'Allianz Italy', 'Allianz Spain', 'Allianz Poland', 'Allianz Austria', 'Allianz Switzerland',
    'Allianz Belgium', 'Allianz Benelux', 'Allianz Reinsurance', 'Allianz Partners', 'Allianz Direct',
    'Allianz Trade', 'Allianz Commercial', 'Allianz UK', 'Allianz SE',
];

const PHONE_REGEX = /^\+[\d\s()./-]{7,20}$/;
const GRM_REGEX = /^[A-Za-z]{4}[0-9]{7}$/;

const validatePhone = (value) => {
    if (!value) return 'Phone number is required';
    if (!PHONE_REGEX.test(value.trim())) return 'Format: +XX (XXX) XXX-XXXX';
    return null;
};

const validateEmail = (value) => {
    if (!value) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Enter a valid email address';
    return null;
};

// Structural counts (not pricing) — safe to keep client-side per the FE spec.
const WIFI_SQFT_PER_AP = 1100;
const SQM_TO_SQFT = 10.7639;
const LAN_SPARE_PCT = 0.25;
// Wireframe placeholder unit prices for the LAN/WiFi mini-cost boxes — not sourced
// from the backend pricing engine. Preliminary display only, per stakeholder guidance.
const LAN_UNIT_PRICE = 9.94;
const WIFI_UNIT_PRICE = 65.78;

const calcWifiApCount = (floorSqm) => {
    const sqm = parseFloat(floorSqm) || 0;
    if (!sqm) return 0;
    return Math.ceil((sqm * SQM_TO_SQFT) / WIFI_SQFT_PER_AP);
};

const calcLanPortCount = ({ users, phones, vc, aps, staticDevices }) => {
    const base = (parseFloat(users) || 0) + (parseFloat(phones) || 0) + (parseFloat(vc) || 0) +
        (parseFloat(aps) || 0) + (parseFloat(staticDevices) || 0);
    if (!base) return 0;
    return Math.ceil(base * (1 + LAN_SPARE_PCT));
};

// WAN Link Type selection rule (structural — not pricing).
const WAN_LINK_COMBOS = [
    { value: '2xDIA', label: '2× DIA', circuits: 2 },
    { value: '1DIA+1BB', label: '1 DIA + 1 Broadband', circuits: 2 },
    { value: '1Broadband', label: '1 Broadband', circuits: 1 },
];
const suggestWanLinkValue = (siteType, numUsers) => {
    if (siteType === 'Data Center') return '2xDIA';
    const u = parseFloat(numUsers) || 0;
    if (u > 50) return '2xDIA';
    if (u >= 5) return '1DIA+1BB';
    return '1Broadband';
};

const FIELD_SX = {
    '& .MuiFormHelperText-root': { minHeight: '1.25em' },
};

// Wireframe-style section header: small, uppercase, bottom border.
const SectionLabel = ({ children, sx }) => (
    <Typography sx={{
        fontSize: 11, fontWeight: 700, color: '#000', textTransform: 'uppercase',
        letterSpacing: '0.8px', mb: 2, pb: 0.75, borderBottom: '1px solid #f0f0f0',
        ...sx,
    }}>
        {children}
    </Typography>
);

const SubDivider = ({ children }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, my: 2 }}>
        <Box sx={{ flex: 1, height: '1px', bgcolor: '#f0f0f0' }} />
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#003781', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
            {children}
        </Typography>
        <Box sx={{ flex: 1, height: '1px', bgcolor: '#f0f0f0' }} />
    </Box>
);

const InfoTip = ({ children }) => (
    <Tooltip
        title={<Box sx={{ p: 0.5, maxWidth: 320 }}>{children}</Box>}
        arrow
        placement="right"
    >
        <HelpOutlineIcon sx={{ fontSize: 15, color: '#003781', verticalAlign: 'middle', ml: 0.5, cursor: 'help' }} />
    </Tooltip>
);

// Mini per-section cost box, matching the wireframe's mini-cost-box exactly.
// Unit prices are the same wireframe placeholders (€9.94/port, €65.78/AP) — not
// sourced from the backend pricing engine, so treat these as preliminary only.
const MiniCostBox = ({ tag, title, type = 'Monthly', amount, desc, note }) => (
    <Box sx={{ border: '1px solid #e0e7ef', borderRadius: 1, p: 2, bgcolor: '#f8f9fc', mt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box sx={{ bgcolor: '#e3ebf7', color: '#003781', fontSize: 11, fontWeight: 700, px: 1, py: 0.25, borderRadius: 5 }}>{tag}</Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{title}</Typography>
            <Typography sx={{ fontSize: 11, color: '#999', ml: 'auto' }}>{type}</Typography>
        </Box>
        <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#003781', mb: 0.5 }}>€{amount.toFixed(2)}</Typography>
        <Typography sx={{ fontSize: 12, color: '#666' }}>{desc}</Typography>
        {note && <Typography sx={{ fontSize: 11, color: '#8a6100', fontStyle: 'italic', mt: 1 }}>{note}</Typography>}
    </Box>
);

// Single consolidated WAN estimate card — one headline figure (a range when the pricing
// engine returns more than one provider estimate) and one breakdown table, rather than an
// accordion per provider. Shared by the form and its Confirm Submit dialog mirror.
const WanCostEstimateCard = ({ costEstimate, costEstimateLoading }) => {
    if (costEstimateLoading) return <CircularProgress size={20} />;
    if (!costEstimate?.estimates?.length) {
        return <Alert severity="info">Enter Country and Bandwidth above to see a cost estimate.</Alert>;
    }
    const estimates = costEstimate.estimates;
    const totals = estimates.map(est => est.total || 0);
    const minTotal = Math.min(...totals);
    const maxTotal = Math.max(...totals);
    const isRange = maxTotal > minTotal;
    const primary = estimates[0];
    const rows = Object.entries(primary.breakdown || {}).filter(([key]) => !HIDDEN_BREAKDOWN_KEYS.includes(key));

    return (
        <Box>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#003781' }}>
                {isRange ? `€${minTotal.toFixed(2)} – €${maxTotal.toFixed(2)}` : `€${minTotal.toFixed(2)}`}
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#888', mb: 1.5 }}>
                {isRange ? 'estimated range (WAN only)' : 'estimated total (WAN only)'}
            </Typography>
            <Accordion disableGutters elevation={0} sx={{ border: '1px solid #e0e0e0', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, background: '#fafafa' }}>View Cost Breakdown</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700, color: '#666', fontSize: 12 ,background:'#f5f7fa'}}>Component</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, color: '#666', fontSize: 12 ,background:'#f5f7fa'}}>Amount</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#666', fontSize: 12 ,background:'#f5f7fa'}}>Type</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map(([key, val]) => (
                                    <TableRow key={key}>
                                        <TableCell>{COST_BREAKDOWN_LABELS[key] || key}</TableCell>
                                        <TableCell align="right">€{Number(val).toFixed(2)}</TableCell>
                                        <TableCell sx={{ color: '#999' }}>{COST_BREAKDOWN_TYPES[key] || ''}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700, color: '#003781', background: '#fafeff' }}>WAN Total</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, color: '#003781', background: '#fafeff' }}>€{Number(primary.total || 0).toFixed(2)}</TableCell>
                                    <TableCell sx={{ background: '#fafeff' }} />
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Alert severity="info" sx={{ m: 1.5, fontSize: 12 }}>
                        LAN and WiFi shown are preliminary unit-price estimates. Actual costs confirmed once WOP is submitted.
                    </Alert>
                    <Typography variant="caption" sx={{ color: '#666', fontStyle: 'italic', display: 'block', mt: 0.5, px: 1.5, pb: 1 }}>
                        Preliminary WAN-only estimate — final cost determined after  Work Order Proposal (WOP).
                    </Typography>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

// Backs onto POST/GET/DELETE /api/new-sites/{id}/attachments, which requires the request
// to already have an id. So a file picked before the first save is held as `pendingFile`
// (not yet uploaded) and the parent flushes it via uploadPendingAttachments() right after
// create/update returns an id — letting the user attach files while still filling the form,
// same as before, while the actual bytes only ever go over the wire once a row exists.
const AttachmentUploader = ({ kind, label, attachments, pendingFile, disabled, required, error, uploadError, uploading, canDelete, helperText, onUpload, onDelete, onRemovePending }) => {
    const items = attachments.filter(a => a.kind === kind);
    const showError = error || !!uploadError;
    return (
        <Box sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: 12, color: showError ? '#d32f2f' : '#666', fontWeight: 500, mb: 0.75 }}>
                {label}{required ? ' *' : ''}
            </Typography>
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
                ...(showError ? { border: '1px solid #d32f2f', borderRadius: 1, p: 1 } : {}),
            }}>
                <Button
                    component="label" variant="outlined" size="small" disabled={disabled || uploading}
                    startIcon={uploading ? <CircularProgress size={14} /> : <AttachFileIcon fontSize="small" />}
                    sx={{ borderColor: '#003781', color: '#003781', '&:hover': { bgcolor: '#f0f4ff' } }}
                >
                    {uploading ? 'Uploading…' : 'Attach'}
                    <input
                        type="file" hidden
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            e.target.value = '';
                            if (file) onUpload(file);
                        }}
                    />
                </Button>
                {items.length === 0 && !pendingFile && (
                    <Typography sx={{ fontSize: 12, color: '#888' }}>No file selected</Typography>
                )}
                {pendingFile && (
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#fff4e0', color: '#8a6100',
                        borderRadius: 5, px: 1, py: 0.25,
                    }}>
                        <Typography sx={{ fontSize: 12 }}>{pendingFile.name} </Typography>
                        <Box
                            component="span" onClick={onRemovePending}
                            sx={{ cursor: 'pointer', color: '#b3261e', fontWeight: 700, fontSize: 12, lineHeight: 1, px: 0.5 }}
                        >
                            ×
                        </Box>
                    </Box>
                )}
                {items.map((a) => (
                    <Box key={a.file_name} sx={{
                        display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#eef4ec', color: '#2e7d32',
                        borderRadius: 5, px: 1, py: 0.25,
                    }}>
                        <Typography sx={{ fontSize: 12 }}>{a.file_name}</Typography>
                        {canDelete && (
                            <Box
                                component="span" onClick={() => onDelete(a.file_name)}
                                sx={{ cursor: 'pointer', color: '#b3261e', fontWeight: 700, fontSize: 12, lineHeight: 1, px: 0.5 }}
                            >
                                ×
                            </Box>
                        )}
                    </Box>
                ))}
            </Box>
            <Typography sx={{ fontSize: 11, color: showError ? '#d32f2f' : '#777', mt: 0.5 }}>
                {uploadError || (error ? 'This attachment is mandatory' : helperText)}
            </Typography>
        </Box>
    );
};

const NewSiteOrderingForm = ({
    control,
    watch,
    setValue,
    getValues,
    trigger,
    handleSubmit,
    userRole,
    isUpdateMode,
    updateId,
    externalDemandData,
}) => {
    const navigate = useNavigate();
    const { accounts } = useMsal();
    const userName = accounts[0]?.name || '';

    const [servicesOn, setServicesOn] = useState({ WAN: true, LAN: true, WiFi: true, Voice: true });
    const [coloc, setColoc] = useState(false);
    const [colocOes, setColocOes] = useState([]);

    const [localData, setLocalData] = useState({
        cost_centre: '',
        debtor: '',
        oe_name: '',
        site_address: '',
        country: '',
        city: '',
        wave: '',
        site_type: 'Normal Site',
        desired_installation_date: '',
        expedite_status: 'BAU',

        num_users: '',
        num_standard_phones: '',
        num_wan_links: '',
        wan_link_type: '',
        bandwidth_mbps: '',
        connection_type: '',
        connection_type_2: '',
        circuit_already_ordered: false,
        circuit_demand_number: '',

        num_floors: '',
        lan_static_devices: '',
        lan_users_per_floor: '',
        num_lan_ports: '',

        wifi_survey_done: 'No',
        heatmap_attached: false,
        survey_result_attached: false,
        wifi_floor_dim_sqm: '',
        num_wifi_ap: '',
        floor_plan_attached: false,
        site_ready_for_impl: 'No',
        site_readiness_date: '',
        fe_required: 'No',
        floor_walk_done: 'No',

        wifi_floors_in_scope: '',
        wifi_areas_in_scope: '',
        wifi_ceiling_height: '',
        wifi_device_types_counts: '',
        wifi_coverage_in_scope: '',
        wifi_coverage_out_of_scope: '',
        wifi_critical_areas: '',
        wifi_data_usage_type: '',
        wifi_wall_types_pillars: '',

        num_premium_phones: '',
        num_analogue_phones: '',
        num_vc_devices: '',
        num_lan_comm_rooms: '',
        num_conference_devices: '',
        ddi_moved_or_procured: '',
        ddi_count: '',
        ddi_ported_count: '',
        ddi_solution: '',
        ddi_shared_with_cc: 'No',
        ivr_arc_console_required: 'No',
        hunt_group_required: 'No',
        pickup_group_required: 'No',

        request_description: '',
        business_reasons: '',

        customer_name: userName,
        customer_contact_mail: userRole?.userEmail || '',
        customer_contact_phone: '',
        site_technical_user_name: '',
        site_technical_user_email: '',
        site_technical_user_phone: '',
    });

    const [manualWanLink, setManualWanLink] = useState(false);
    const [manualBw, setManualBw] = useState(false);

    const [countryCityData, setCountryCityData] = useState({ countries: [], cities: {}, country_names: {}, city_names: {} });
    const [countryCityLoading, setCountryCityLoading] = useState(false);
    const [siteIdGenerating, setSiteIdGenerating] = useState(false);
    const [siteId, setSiteId] = useState('');

    const [fieldErrors, setFieldErrors] = useState({});
    const [servicesError, setServicesError] = useState(false);
    const [colocOesError, setColocOesError] = useState(false);
    const [floorPlanError, setFloorPlanError] = useState(false);

    const [attachments, setAttachments] = useState([]);
    const [attachmentUploading, setAttachmentUploading] = useState({});
    const [attachmentErrors, setAttachmentErrors] = useState({});
    // Files picked before the request has an id yet — flushed by uploadPendingAttachments()
    // once handleSaveDraft/handleConfirmedSubmit obtains one.
    const [pendingAttachments, setPendingAttachments] = useState({});

    const [grmDuplicate, setGrmDuplicate] = useState(false);
    const [costEstimate, setCostEstimate] = useState(null);
    const [costEstimateLoading, setCostEstimateLoading] = useState(false);

    const [savedId, setSavedId] = useState(updateId ? Number(updateId) : null);
    const [isSaving, setIsSaving] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogTitle, setDialogTitle] = useState('');
    const [dialogMessage, setDialogMessage] = useState('');
    const [successOpen, setSuccessOpen] = useState(false);
    const [confirmSubmitOpen, setConfirmSubmitOpen] = useState(false);

    // Bound to NewRequest.js's shared "GRM No" field, same as Circuit Ordering — New Site has
    // no GRM input of its own, so localData never saw what the user typed there.
    const grmDemandNumber = watch ? watch('grmDemandNumber') : '';
    const primaryLocationId = watch ? watch('primaryLocationId') : '';

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setLocalData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        setFieldErrors(prev => (prev[name] ? { ...prev, [name]: undefined } : prev));
    };

    const toggleService = (name) => {
        setServicesOn(prev => {
            const next = { ...prev, [name]: !prev[name] };
            // At least one service must stay selected.
            if (!Object.values(next).some(Boolean)) return prev;
            return next;
        });
        setServicesError(false);
    };

    // ── Country / city (reuse the same TEM lookups Circuit Ordering uses) ──
    useEffect(() => {
        const load = async () => {
            setCountryCityLoading(true);
            try {
                const res = await ApiService.temCountriesCities();
                setCountryCityData({
                    countries: res.countries || [],
                    cities: res.cities || {},
                    country_names: res.country_names || {},
                    city_names: res.city_names || {},
                });
            } catch (e) {
                console.error('Failed to load countries/cities:', e);
            } finally {
                setCountryCityLoading(false);
            }
        };
        load();
    }, []);

    // ── Site ID auto-generation from Country + City ──
    useEffect(() => {
        if (!localData.country || !localData.city) {
            setSiteId('');
            return;
        }
        let cancelled = false;
        setSiteIdGenerating(true);
        ApiService.generateSiteId(localData.country, localData.city)
            .then(res => { if (!cancelled) setSiteId(res.site_id || ''); })
            .catch(() => { if (!cancelled) setSiteId(''); })
            .finally(() => { if (!cancelled) setSiteIdGenerating(false); });
        return () => { cancelled = true; };
    }, [localData.country, localData.city]);

    // ── WAN Link Type auto-suggestion ──
    useEffect(() => {
        if (!servicesOn.WAN || manualWanLink) return;
        const suggested = suggestWanLinkValue(localData.site_type, localData.num_users);
        const combo = WAN_LINK_COMBOS.find(c => c.value === suggested);
        setLocalData(prev => ({
            ...prev,
            wan_link_type: suggested,
            num_wan_links: combo ? String(combo.circuits) : prev.num_wan_links,
        }));
    }, [servicesOn.WAN, manualWanLink, localData.site_type, localData.num_users]);

    const onManualWanLink = (e) => {
        const value = e.target.value;
        setManualWanLink(true);
        const combo = WAN_LINK_COMBOS.find(c => c.value === value);
        setLocalData(prev => ({ ...prev, wan_link_type: value, num_wan_links: combo ? String(combo.circuits) : prev.num_wan_links }));
        setFieldErrors(prev => (prev.wan_link_type ? { ...prev, wan_link_type: undefined } : prev));
    };

    // ── Bandwidth auto-calc from Users / Phones / VC (structural entry, not pricing) ──
    // The calculated value is the floor: a manual entry may raise it but never go below it.
    const calcBw = Math.ceil(((parseFloat(localData.num_users) || 0) * 0.768
        + (parseFloat(localData.num_standard_phones) || 0) * 0.064
        + (parseFloat(localData.num_vc_devices) || 0) * 2) * 1.1);
    const bwBelowMin = servicesOn.WAN && calcBw > 0 && localData.bandwidth_mbps !== ''
        && parseFloat(localData.bandwidth_mbps) < calcBw;
    const bwMinMsg = `Required Bandwidth cannot be less than the calculated value (${calcBw} Mbps)`;

    useEffect(() => {
        if (!servicesOn.WAN || !calcBw) return;
        // Auto mode always follows the calculation; manual mode is only lifted if the counts rise above it.
        setLocalData(prev => {
            const cur = parseFloat(prev.bandwidth_mbps);
            if (manualBw && cur >= calcBw) return prev;
            return { ...prev, bandwidth_mbps: String(calcBw) };
        });
    }, [servicesOn.WAN, manualBw, calcBw]);

    const onManualBw = (e) => {
        setManualBw(true);
        handleChange(e);
    };

    // ── LAN port count ──
    useEffect(() => {
        if (!servicesOn.LAN) return;
        const aps = servicesOn.WiFi ? calcWifiApCount(localData.wifi_floor_dim_sqm) : 0;
        const ports = calcLanPortCount({
            users: localData.num_users,
            phones: localData.num_standard_phones,
            vc: localData.num_vc_devices,
            aps,
            staticDevices: localData.lan_static_devices,
        });
        setLocalData(prev => ({ ...prev, num_lan_ports: ports ? String(ports) : '' }));
    }, [servicesOn.LAN, servicesOn.WiFi, localData.num_users, localData.num_standard_phones, localData.num_vc_devices, localData.lan_static_devices, localData.wifi_floor_dim_sqm]);

    // ── WiFi AP count ──
    useEffect(() => {
        if (!servicesOn.WiFi) return;
        const aps = calcWifiApCount(localData.wifi_floor_dim_sqm);
        setLocalData(prev => ({ ...prev, num_wifi_ap: aps ? String(aps) : '' }));
    }, [servicesOn.WiFi, localData.wifi_floor_dim_sqm]);

    // ── Debounced backend cost estimate ──
    useEffect(() => {
        if (!localData.country || !localData.bandwidth_mbps) {
            setCostEstimate(null);
            return;
        }
        const handle = setTimeout(() => {
            setCostEstimateLoading(true);
            ApiService.newSiteCostEstimate({
                country: localData.country,
                site_type: localData.site_type,
                bandwidth_mbps: localData.bandwidth_mbps,
                num_wan_links: servicesOn.WAN ? localData.num_wan_links : undefined,
                num_lan_ports: servicesOn.LAN ? localData.num_lan_ports : undefined,
                num_wifi_ap: servicesOn.WiFi ? localData.num_wifi_ap : undefined,
            })
                .then(res => setCostEstimate(res))
                .catch(() => setCostEstimate(null))
                .finally(() => setCostEstimateLoading(false));
        }, 500);
        return () => clearTimeout(handle);
    }, [localData.country, localData.site_type, localData.bandwidth_mbps, localData.num_wan_links,
        localData.num_lan_ports, localData.num_wifi_ap, servicesOn.WAN, servicesOn.LAN, servicesOn.WiFi]);

    // ── GRM duplicate check ──
    const handleGrmCheck = async () => {
        if (!grmDemandNumber) { setGrmDuplicate(false); return; }
        try {
            const res = await ApiService.checkGrmNumberNewSite(grmDemandNumber);
            const ownId = updateId || savedId;
            const matchesSelf = ownId && res.request_id != null && String(res.request_id) === String(ownId) && res.request_type === 'New Site Ordering';
            setGrmDuplicate(!!res.exists && !matchesSelf);
        } catch (_) { /* empty */ }
    };

    useEffect(() => { handleGrmCheck(); /* re-check whenever GRM changes */ }, [grmDemandNumber]);

    const validateGrmFormat = (value) => {
        if (!value) return null;
        return GRM_REGEX.test(value) ? null : 'Invalid format. Must be 4 letters followed by 7 numbers (e.g. DMND1234567).';
    };

    // ── Prefill from login / GRM redirect — editable, so only fills a blank field ──
    useEffect(() => {
        if (userRole?.userEmail) {
            setLocalData(prev => ({ ...prev, customer_contact_mail: prev.customer_contact_mail || userRole.userEmail }));
        }
        if (userName) {
            setLocalData(prev => ({ ...prev, customer_name: prev.customer_name || userName }));
        }
    }, [userRole?.userEmail, userName]);

    useEffect(() => {
        if (!externalDemandData) return;
        if (setValue) {
            const grm = externalDemandData.grm_demand_number || externalDemandData.grm_no;
            if (grm) setValue('grmDemandNumber', grm);
        }
        setLocalData(prev => ({
            ...prev,
            cost_centre: externalDemandData.cost_centre || prev.cost_centre,
            debtor: externalDemandData.debtor || prev.debtor,
            oe_name: externalDemandData.oe_name || prev.oe_name,
            request_description: externalDemandData.description_of_odm_request || prev.request_description,
        }));
    }, [externalDemandData, setValue]);

    // ── Update mode: load existing request ──
    useEffect(() => {
        if (!isUpdateMode || !updateId) return;
        const load = async () => {
            try {
                const data = await ApiService.getNewSiteOrder(updateId);
                const loadedAttachments = Array.isArray(data.attachments) ? data.attachments : [];
                setAttachments(loadedAttachments);
                setLocalData(prev => ({
                    ...prev,
                    ...data,
                    lan_static_devices: data.num_static_devices ?? prev.lan_static_devices,
                    lan_users_per_floor: data.num_users_per_floor ?? prev.lan_users_per_floor,
                    wifi_floor_dim_sqm: data.floor_area_sqm ?? prev.wifi_floor_dim_sqm,
                    hunt_group_required: data.hunt_group_required ? 'Yes' : 'No',
                    pickup_group_required: data.pickup_group_required ? 'Yes' : 'No',
                    ivr_arc_console_required: (data.arc_console_required || data.ivr_required) ? 'Yes' : 'No',
                    ddi_shared_with_cc: data.ddi_shared_with_cc ? 'Yes' : 'No',
                    floor_walk_done: data.floor_walk_done ? 'Yes' : 'No',
                    survey_result_attached: loadedAttachments.some(a => a.kind === 'survey_result'),
                }));
                if (setValue && data.primary_location_id) setValue('primaryLocationId', data.primary_location_id);
                if (setValue) setValue('grmDemandNumber', data.grm_demand_number || '');
                if (data.city) setSiteId(data.site_id || '');

                const selectedServices = Array.isArray(data.services_required)
                    ? data.services_required
                    : [];
                setServicesOn(prev => ({
                    WAN: selectedServices.length ? selectedServices.includes('WAN') : (!!(data.num_wan_links || data.bandwidth_mbps) || prev.WAN),
                    LAN: selectedServices.length ? selectedServices.includes('LAN') : (!!(data.num_lan_ports || data.num_floors) || prev.LAN),
                    WiFi: selectedServices.length ? selectedServices.includes('WiFi') : (!!(data.num_wifi_ap || data.floor_area_sqm) || prev.WiFi),
                    Voice: selectedServices.length ? selectedServices.includes('Voice') : (!!(data.num_premium_phones || data.num_analogue_phones || data.num_vc_devices) || prev.Voice),
                }));
                setColoc(!!data.is_colocated);
                setColocOes(Array.isArray(data.colocated_oes) ? data.colocated_oes : []);
            } catch (e) {
                console.error('Error loading new site order:', e);
            }
        };
        load();
    }, [isUpdateMode, updateId]);

    const _toFloat = v => { const n = parseFloat(v); return isNaN(n) ? null : n; };
    const _toInt = v => { const n = parseInt(v, 10); return isNaN(n) ? null : n; };

    const buildPayload = () => ({
        ...localData,
        odm_title: (watch && watch('odmTitle')) || '',
        grm_demand_number: grmDemandNumber || '',
        primary_location_id: primaryLocationId || '',
        request_type: 'new_site_ordering',
        is_colocated: coloc,
        colocated_oes: coloc ? colocOes : [],
        services_required: Object.keys(servicesOn).filter(k => servicesOn[k]),
        num_users: _toInt(localData.num_users),
        num_wan_links: servicesOn.WAN ? _toInt(localData.num_wan_links) : null,
        bandwidth_mbps: servicesOn.WAN ? _toFloat(localData.bandwidth_mbps) : null,
        num_floors: servicesOn.LAN ? _toInt(localData.num_floors) : null,
        num_users_per_floor: servicesOn.LAN ? _toInt(localData.lan_users_per_floor) : null,
        num_static_devices: servicesOn.LAN ? _toInt(localData.lan_static_devices) : null,
        num_lan_ports: servicesOn.LAN ? _toInt(localData.num_lan_ports) : null,
        floor_area_sqm: servicesOn.WiFi ? _toFloat(localData.wifi_floor_dim_sqm) : null,
        num_wifi_ap: servicesOn.WiFi ? _toInt(localData.num_wifi_ap) : null,
        wifi_floors_in_scope: servicesOn.WiFi ? localData.wifi_floors_in_scope : null,
        wifi_areas_in_scope: servicesOn.WiFi ? localData.wifi_areas_in_scope : null,
        wifi_ceiling_height: servicesOn.WiFi ? localData.wifi_ceiling_height : null,
        wifi_device_types_counts: servicesOn.WiFi ? localData.wifi_device_types_counts : null,
        wifi_coverage_in_scope: servicesOn.WiFi ? localData.wifi_coverage_in_scope : null,
        wifi_coverage_out_of_scope: servicesOn.WiFi ? localData.wifi_coverage_out_of_scope : null,
        wifi_critical_areas: servicesOn.WiFi ? localData.wifi_critical_areas : null,
        wifi_data_usage_type: servicesOn.WiFi ? localData.wifi_data_usage_type : null,
        wifi_wall_types_pillars: servicesOn.WiFi ? localData.wifi_wall_types_pillars : null,
        floor_walk_done: localData.floor_walk_done === 'Yes',
        num_premium_phones: servicesOn.Voice ? _toInt(localData.num_premium_phones) : null,
        num_analogue_phones: servicesOn.Voice ? _toInt(localData.num_analogue_phones) : null,
        num_vc_devices: servicesOn.Voice ? _toInt(localData.num_vc_devices) : null,
        num_conference_devices: servicesOn.Voice ? _toInt(localData.num_conference_devices) : null,
        num_lan_comm_rooms: servicesOn.LAN ? _toInt(localData.num_lan_comm_rooms) : null,
        ddi_count: servicesOn.Voice ? _toInt(localData.ddi_count) : null,
        ddi_ported_count: servicesOn.Voice ? _toInt(localData.ddi_ported_count) : null,
        ddi_shared_with_cc: servicesOn.Voice ? localData.ddi_shared_with_cc === 'Yes' : null,
        hunt_group_required: servicesOn.Voice ? localData.hunt_group_required === 'Yes' : null,
        pickup_group_required: servicesOn.Voice ? localData.pickup_group_required === 'Yes' : null,
        arc_console_required: servicesOn.Voice ? localData.ivr_arc_console_required === 'Yes' : null,
        ivr_required: servicesOn.Voice ? localData.ivr_arc_console_required === 'Yes' : null,
        connection_type_2: servicesOn.WAN ? localData.connection_type_2 : null,
        request_details_json: JSON.stringify({
            services_selected: Object.keys(servicesOn).filter(k => servicesOn[k]),
        }),
    });

    const handleSaveDraft = async () => {
        if (grmDuplicate) {
            setDialogTitle('Validation Error');
            setDialogMessage('This GRM Demand Number already exists. Please check with your team.');
            setDialogOpen(true);
            return;
        }
        setIsSaving(true);
        try {
            const payload = buildPayload();
            let id = updateId || savedId;
            if (isUpdateMode && id) {
                await ApiService.updateNewSiteOrder(id, payload);
                setDialogTitle('Saved');
                setDialogMessage('New Site Ordering draft updated successfully.');
            } else {
                const result = await ApiService.createNewSiteOrder(payload);
                id = result.id;
                setSavedId(id);
                setDialogTitle('Saved');
                setDialogMessage('New Site Ordering request saved as draft.');
            }
            await uploadPendingAttachments(id);
            ApiService.invalidateRequestsCache && ApiService.invalidateRequestsCache();
            setDialogOpen(true);
        } catch (e) {
            setDialogTitle('Error');
            setDialogMessage('Failed to save draft: ' + e.message);
            setDialogOpen(true);
        } finally {
            setIsSaving(false);
        }
    };

    const isDraftState = !localData.request_state || localData.request_state === 'Draft';

    const markAttachedLocally = (kind, attached) => {
        if (kind === 'heatmap') setLocalData(prev => ({ ...prev, heatmap_attached: attached }));
        if (kind === 'floor_plan') {
            setLocalData(prev => ({ ...prev, floor_plan_attached: attached }));
            if (attached) setFloorPlanError(false);
        }
        if (kind === 'survey_result') setLocalData(prev => ({ ...prev, survey_result_attached: attached }));
    };

    const handleAttachmentUpload = async (kind, file) => {
        const requestId = updateId || savedId;
        setAttachmentUploading(prev => ({ ...prev, [kind]: true }));
        setAttachmentErrors(prev => ({ ...prev, [kind]: null }));
        try {
            const result = await ApiService.uploadNewSiteAttachment(requestId, file, kind);
            setAttachments(Array.isArray(result.attachments) ? result.attachments : []);
            markAttachedLocally(kind, true);
        } catch (e) {
            setAttachmentErrors(prev => ({ ...prev, [kind]: e.message }));
        } finally {
            setAttachmentUploading(prev => ({ ...prev, [kind]: false }));
        }
    };

    // Lets the user pick a file before the request has ever been saved: hold it in
    // memory and mark the readiness flag immediately, matching the old checkbox UX.
    const handleAttachmentPick = (kind, file) => {
        const requestId = updateId || savedId;
        if (requestId) {
            handleAttachmentUpload(kind, file);
        } else {
            setPendingAttachments(prev => ({ ...prev, [kind]: file }));
            setAttachmentErrors(prev => ({ ...prev, [kind]: null }));
            markAttachedLocally(kind, true);
        }
    };

    const handleRemovePending = (kind) => {
        setPendingAttachments(prev => {
            const next = { ...prev };
            delete next[kind];
            return next;
        });
        markAttachedLocally(kind, false);
    };

    // Flushes anything picked before the request existed. Called right after
    // create/update returns an id, so the files ride along with the first save.
    const uploadPendingAttachments = async (requestId) => {
        const pending = Object.entries(pendingAttachments).filter(([, file]) => !!file);
        if (pending.length === 0) return;
        for (const [kind, file] of pending) {
            try {
                const result = await ApiService.uploadNewSiteAttachment(requestId, file, kind);
                setAttachments(Array.isArray(result.attachments) ? result.attachments : []);
            } catch (e) {
                setAttachmentErrors(prev => ({ ...prev, [kind]: e.message }));
            }
        }
        setPendingAttachments({});
    };

    const handleAttachmentDelete = async (fileName, kind) => {
        const requestId = updateId || savedId;
        if (!requestId) return;
        try {
            const result = await ApiService.deleteNewSiteAttachment(requestId, fileName);
            const remaining = Array.isArray(result.attachments) ? result.attachments : [];
            setAttachments(remaining);
            const stillHasKind = remaining.some(a => a.kind === kind);
            if (!stillHasKind) markAttachedLocally(kind, false);
        } catch (e) {
            setDialogTitle('Error');
            setDialogMessage(`Failed to remove attachment: ${e.message}`);
            setDialogOpen(true);
        }
    };

    const handleFormSubmit = async () => {
        const missing = [];
        const errors = {};
        let hasServicesError = false;
        let hasColocOesError = false;
        let hasFloorPlanError = false;

        if (!Object.values(servicesOn).some(Boolean)) { missing.push('Services Required (select at least one)'); hasServicesError = true; }
        if (!localData.oe_name) { missing.push('OE Name'); errors.oe_name = 'OE Name is required'; }
        if (!localData.debtor) { missing.push('Debtor'); errors.debtor = 'Debtor is required'; }
        else if (!/^\d{10}$/.test(localData.debtor)) { missing.push('Debtor (must be 10 digits)'); errors.debtor = 'Debtor must be exactly 10 digits'; }
        if (!localData.cost_centre) { missing.push('Cost Centre'); errors.cost_centre = 'Cost Centre is required'; }
        else if (!/^\d{10}$/.test(localData.cost_centre)) { missing.push('Cost Centre (must be 10 digits)'); errors.cost_centre = 'Cost Centre must be exactly 10 digits'; }
        if (!localData.country) { missing.push('Country'); errors.country = 'Country is required'; }
        if (!localData.city) { missing.push('City'); errors.city = 'City is required'; }
        if (!primaryLocationId) missing.push('Primary Location ID');
        if (!localData.site_address) { missing.push('Site Address'); errors.site_address = 'Site Address is required'; }
        if (!localData.desired_installation_date) { missing.push('Desired Installation Date'); errors.desired_installation_date = 'Desired Installation Date is required'; }
        else if (localData.desired_installation_date < MIN_INSTALL_DATE) { missing.push('Desired Installation Date'); errors.desired_installation_date = INSTALL_DATE_MSG; }
        if (coloc && colocOes.length === 0) { missing.push('Other OEs at this Site'); hasColocOesError = true; }

        if (servicesOn.WAN) {
            if (!localData.wan_link_type) { missing.push('Link Type (WAN)'); errors.wan_link_type = 'Link Type is required'; }
            if (!localData.num_users) { missing.push('Number of Users (WAN)'); errors.num_users = 'Number of Users is required'; }
            if (!localData.num_standard_phones) { missing.push('Number of Phones (WAN)'); errors.num_standard_phones = 'Number of Phones is required'; }
            if (!localData.num_vc_devices) { missing.push('Number of VC Devices (WAN)'); errors.num_vc_devices = 'Number of VC Devices is required'; }
            if (!localData.bandwidth_mbps) { missing.push('Required Bandwidth (WAN)'); errors.bandwidth_mbps = 'Required Bandwidth is required'; }
            else if (bwBelowMin) { missing.push('Required Bandwidth (WAN)'); errors.bandwidth_mbps = bwMinMsg; }
        }
        if (servicesOn.LAN) {
            if (!localData.num_floors) { missing.push('Number of Floors (LAN)'); errors.num_floors = 'Number of Floors is required'; }
            if (!localData.lan_static_devices) { missing.push('Static Devices (LAN)'); errors.lan_static_devices = 'Static Devices is required'; }
            if (!localData.lan_users_per_floor) { missing.push('Number of Users per Floor (LAN)'); errors.lan_users_per_floor = 'Number of Users per Floor is required'; }
        }
        if (servicesOn.WiFi) {
            if (!localData.wifi_survey_done) { missing.push('WiFi Survey Completed? (WiFi)'); errors.wifi_survey_done = 'This field is required'; }
            if (!localData.wifi_floor_dim_sqm) { missing.push('Floor Dimension (WiFi)'); errors.wifi_floor_dim_sqm = 'Floor Dimension is required'; }
            if (!localData.floor_plan_attached) { missing.push('Attach Floor Plan (WiFi section)'); hasFloorPlanError = true; }
        }
        if (servicesOn.Voice) {
            if (!localData.ddi_moved_or_procured) { missing.push('New DDI Required? (Voice)'); errors.ddi_moved_or_procured = 'This field is required'; }
        }

        if (!localData.request_description) { missing.push('Description of ODM Request'); errors.request_description = 'Description of ODM Request is required'; }
        if (!localData.customer_contact_phone) { missing.push('Customer Contact Phone'); errors.customer_contact_phone = 'Customer Contact Phone is required'; }
        if (!localData.site_technical_user_name) { missing.push('Technical Contact Name'); errors.site_technical_user_name = 'Technical Contact Name is required'; }
        if (!localData.site_technical_user_email) { missing.push('Technical Contact Email'); errors.site_technical_user_email = 'Technical Contact Email is required'; }
        if (!localData.site_technical_user_phone) { missing.push('Technical Contact Number'); errors.site_technical_user_phone = 'Technical Contact Number is required'; }

        if (validateGrmFormat(grmDemandNumber)) missing.push('GRM No. (invalid format)');

        setFieldErrors(errors);
        setServicesError(hasServicesError);
        setColocOesError(hasColocOesError);
        setFloorPlanError(hasFloorPlanError);
        if (trigger) trigger();

        if (missing.length > 0) {
            setDialogTitle('Validation Error');
            setDialogMessage(`Please fill in all required fields: ${missing.join(', ')}.`);
            setDialogOpen(true);
            return;
        }
        const phoneErr = validatePhone(localData.customer_contact_phone) || validatePhone(localData.site_technical_user_phone);
        if (phoneErr) {
            setFieldErrors(prev => ({
                ...prev,
                customer_contact_phone: validatePhone(localData.customer_contact_phone) || undefined,
                site_technical_user_phone: validatePhone(localData.site_technical_user_phone) || undefined,
            }));
            setDialogTitle('Validation Error');
            setDialogMessage('Please correct phone number format: +XX (XXX) XXX-XXXX');
            setDialogOpen(true);
            return;
        }
        if (validateEmail(localData.site_technical_user_email)) {
            setFieldErrors(prev => ({ ...prev, site_technical_user_email: validateEmail(localData.site_technical_user_email) }));
            setDialogTitle('Validation Error');
            setDialogMessage('Please enter a valid Technical Contact Email.');
            setDialogOpen(true);
            return;
        }
        if (grmDuplicate) {
            setDialogTitle('Validation Error');
            setDialogMessage('This GRM Demand Number already exists. Please check with your team.');
            setDialogOpen(true);
            return;
        }

        setConfirmSubmitOpen(true);
    };

    const handleConfirmedSubmit = async () => {
        setConfirmSubmitOpen(false);
        setIsSubmitting(true);
        try {
            let id = savedId;
            const payload = buildPayload();
            if (isUpdateMode && (updateId || id)) {
                id = updateId || id;
                await ApiService.updateNewSiteOrder(id, payload);
            } else if (!id) {
                const created = await ApiService.createNewSiteOrder(payload);
                id = created.id;
                setSavedId(id);
            }
            await uploadPendingAttachments(id);
            const submitResult = await ApiService.submitNewSiteOrder(id);
            ApiService.invalidateRequestsCache && ApiService.invalidateRequestsCache();
            console.log('Submit result:', submitResult);
            if (submitResult && submitResult.success === false) {
                // Documented 502 case: the request is saved (request_state is
                // "Submitted"), only the Amelia ticket failed. Not a failed save.
                setDialogTitle('Saved, but not started');
                setDialogMessage(submitResult.error);
                setDialogOpen(true);
            } else {
                setSuccessOpen(true);
            }
        } catch (e) {
            setDialogTitle('Error');
            setDialogMessage('Submission failed: ' + e.message);
            setDialogOpen(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        if (dialogTitle === 'Saved' || dialogTitle === 'Saved, but not started') navigate('/track-requests');
    };

    // ── Field builders ──
    const handleDigitsChange = (e) => {
        e.target.value = e.target.value.replace(/\D/g, '').slice(0, 10);
        handleChange(e);
    };

    const tf = (name, label, opts = {}) => {
        const errorMsg = opts.error || fieldErrors[name];
        return (
            <TextField
                fullWidth
                variant="outlined"
                size="medium"
                label={opts.required ? `${label} *` : label}
                name={name}
                value={localData[name] ?? ''}
                onChange={opts.onChange || handleChange}
                multiline={opts.multiline || false}
                rows={opts.rows}
                type={opts.type || 'text'}
                error={!!errorMsg}
                helperText={errorMsg || opts.helperText || ' '}
                placeholder={opts.placeholder}
                InputLabelProps={{ shrink: true }}
                inputProps={opts.maxLength ? { inputMode: 'numeric', maxLength: opts.maxLength } : (opts.min ? { min: opts.min } : undefined)}
                disabled={opts.disabled || false}
                sx={{
                    ...(opts.disabled ? { '& .Mui-disabled': { backgroundColor: '#f5f5f5', WebkitTextFillColor: 'rgba(0,0,0,0.7)' } } : {}),
                    ...FIELD_SX,
                }}
            />
        );
    };

    const sf = (name, label, options, opts = {}) => {
        const labelText = opts.required ? `${label} *` : label;
        const errorMsg = fieldErrors[name];
        return (
            <FormControl fullWidth variant="outlined" size="medium" error={!!errorMsg} sx={FIELD_SX}>
                <InputLabel shrink sx={{ backgroundColor: '#fff', px: 0.5 }}>{labelText}</InputLabel>
                <Select
                    name={name}
                    value={localData[name] ?? ''}
                    onChange={opts.onChange || handleChange}
                    label={labelText}
                    notched
                    displayEmpty
                    disabled={opts.disabled || false}
                    MenuProps={{
                        PaperProps: { style: { maxHeight: 240 } },
                        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                        transformOrigin: { vertical: 'top', horizontal: 'left' },
                    }}
                >
                    {options.map(o => <MenuItem key={o.value || o} value={o.value || o}>{o.label || o}</MenuItem>)}
                </Select>
                <FormHelperText>{errorMsg || opts.helperText || ' '}</FormHelperText>
            </FormControl>
        );
    };

    // Same per-service figures as the LAN/WiFi/Voice MiniCostBoxes, reused in the confirm dialog.
    const lanCost = (parseFloat(localData.num_lan_ports) || 0) * LAN_UNIT_PRICE;
    const wifiCost = (parseFloat(localData.num_wifi_ap) || 0) * WIFI_UNIT_PRICE;
    const voiceDeviceCount = (parseFloat(localData.num_premium_phones) || 0) + (parseFloat(localData.num_analogue_phones) || 0) +
        (parseFloat(localData.num_vc_devices) || 0) + (parseFloat(localData.num_conference_devices) || 0);

    return (
        <>
            <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Location ID required:</strong> A valid Primary Location ID is mandatory to submit this request.
                If the site doesn&apos;t have one yet, create it in ServiceNow (Self-Service &gt; Service Catalog &gt; SIM Services catalogue) before proceeding.
            </Alert>

            {grmDuplicate && (
                <Alert severity="warning" sx={{ mb: 2 }}>GRM Demand Number already exists in the system.</Alert>
            )}

            {/* ── Site Details ──────────────────────────────────────────── */}
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                <SectionLabel>Site Details</SectionLabel>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>{tf('oe_name', 'OE Name', { required: true, placeholder: 'e.g. Allianz Netherlands' })}</Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>{tf('debtor', 'Debtor', { required: true, placeholder: '1234567890', helperText: 'Enter a 10-digit number', maxLength: 10, onChange: handleDigitsChange })}</Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>{tf('cost_centre', 'Cost Centre', { required: true, placeholder: '1234567890', helperText: 'Enter a 10-digit numeric Cost Centre', maxLength: 10, onChange: handleDigitsChange })}</Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        {sf('country', 'Country', (countryCityData.countries || []).map(c => {
                            const fullName = countryCityData.country_names[c];
                            return { value: c, label: fullName ? `${fullName} (${c})` : c };
                        }), { required: true, helperText: countryCityLoading ? 'Loading…' : 'Changing country refreshes cost estimate' })}
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        {sf('city', 'City', (countryCityData.cities[localData.country] || []).map(c => {
                            const fullName = (countryCityData.city_names[localData.country] || {})[c];
                            return { value: c, label: fullName ? `${fullName} (${c})` : c };
                        }), { required: true, disabled: !localData.country, helperText: !localData.country ? 'Select a country first' : ' ' })}
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth variant="outlined" size="medium" label="Site ID" disabled
                            value={siteId} InputLabelProps={{ shrink: true }}
                            helperText={siteIdGenerating ? 'Generating…' : 'Auto-generated from Country + City + Wave mapping'}
                            sx={{ '& .Mui-disabled': { backgroundColor: '#f5f5f5', WebkitTextFillColor: 'rgba(0,0,0,0.7)' }, ...FIELD_SX }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>{tf('site_address', 'Site Address', { required: true, multiline: true, rows: 3, placeholder: 'Full street address of the new site' })}</Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box sx={{ position: 'relative' }}>
                            {sf('site_type', 'Site Type', SITE_TYPE_OPTIONS, { required: true })}
                            <InfoTip>
                                <Typography sx={{ fontWeight: 700, fontSize: 12, mb: 0.5 }}>Site Type Classification</Typography>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ p: 0.5, fontSize: 11, fontWeight: 700 }}>Type</TableCell>
                                            <TableCell sx={{ p: 0.5, fontSize: 11, fontWeight: 700 }}>Description</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {[
                                            ['A', 'Data Center / very large offices'],
                                            ['B', 'HQ office buildings, >1,000 users'],
                                            ['C', 'Large offices, 500–1,000 users'],
                                            ['D', 'Medium offices, 50–500 users'],
                                            ['E', 'Small offices, 5–50 users'],
                                            ['F', 'Very small offices, <5 users'],
                                        ].map(([t, d]) => (
                                            <TableRow key={t}>
                                                <TableCell sx={{ p: 0.5, fontSize: 11 }}>{t}</TableCell>
                                                <TableCell sx={{ p: 0.5, fontSize: 11 }}>{d}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </InfoTip>
                        </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControlLabel
                            control={<Switch checked={coloc} onChange={(e) => setColoc(e.target.checked)} />}
                            label="Is this a co-located site?"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>{tf('desired_installation_date', 'Desired Installation Date', { required: true, type: 'date', min: MIN_INSTALL_DATE, helperText: INSTALL_DATE_MSG })}</Grid>

                    {coloc && (
                        <Grid size={{ xs: 12 }}>
                            <Typography sx={{ fontSize: 12, color: '#777', mb: 1 }}>
                                Select every OE present at this site. A separate ODM request must be raised for each additional OE.
                            </Typography>
                            <Autocomplete
                                multiple
                                options={ALL_OES}
                                value={colocOes}
                                onChange={(e, val) => { setColocOes(val); setColocOesError(false); }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Other OEs at this Site *"
                                        placeholder="Search OEs…"
                                        InputLabelProps={{ shrink: true }}
                                        error={colocOesError}
                                        helperText={colocOesError ? 'Select at least one OE' : ' '}
                                    />
                                )}
                            />
                            <Alert severity="info" sx={{ mt: 1.5, fontSize: 12 }}>
                                Separate ODM to be raised for each OE selected above. OE information here is for design
                                consideration — the request itself must still be raised separately, but we need this
                                information for our records.
                            </Alert>
                        </Grid>
                    )}
                </Grid>
            </Paper>

            {/* ── Services Required ─────────────────────────────────────── */}
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                <SectionLabel sx={{ fontSize: 16, fontWeight: 600 }}>Services Required</SectionLabel>
                <Typography sx={{ fontSize: 12, color: '#777', mb: 1.5 }}>
                    Indicate which services are in scope for this new site. Turning a service off hides its section
                    and excludes it from validation and the cost estimate.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    {SERVICES.map(s => (
                        <Box
                            key={s.key}
                            onClick={() => toggleService(s.key)}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', userSelect: 'none',
                                border: servicesError ? '1.5px solid #d32f2f' : (servicesOn[s.key] ? '1.5px solid #003781' : '1.5px solid #e0e0e0'),
                                bgcolor: servicesOn[s.key] ? '#f0f4ff' : 'transparent',
                                borderRadius: 2, px: 1.75, py: 1, transition: 'all 0.15s',
                            }}
                        >
                            <Checkbox checked={!!servicesOn[s.key]} size="small" sx={{ p: 0 }} tabIndex={-1} />
                            <Box>
                                <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{s.key}</Typography>
                                <Typography sx={{ fontSize: 11, color: '#888' }}>{s.desc}</Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
                {servicesError && (
                    <Typography sx={{ fontSize: 12, color: '#d32f2f', mt: 1 }}>Select at least one service.</Typography>
                )}
            </Paper>

            {/* ── WAN ────────────────────────────────────────────────────── */}
            {servicesOn.WAN && (
                <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                    <SectionLabel sx={{ fontSize: 16, fontWeight: 600 }}>Network Services Requirements — WAN</SectionLabel>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            {sf('site_type', 'Site Type', SITE_TYPE_OPTIONS, { helperText: 'Data Center always gets 2× DIA, regardless of user count' })}
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>{tf('num_wan_links', 'Number of WAN Circuits', { disabled: true, type: 'number', helperText: 'Derived from the selected Link Type configuration' })}</Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Box sx={{ position: 'relative' }}>
                                {sf('wan_link_type', 'Link Type', WAN_LINK_COMBOS, {
                                    required: true, onChange: onManualWanLink,
                                    helperText: manualWanLink ? 'Manually selected — no longer auto-suggested' : 'Suggested from Site Type / Number of Users — edit if you need a different configuration',
                                })}
                                <InfoTip>
                                    <Typography sx={{ fontWeight: 700, fontSize: 12, mb: 0.5 }}>Link Type — Selection Rule</Typography>
                                    <Table size="small">
                                        <TableBody>
                                            {[
                                                ['Site Type = Data Center', '2× DIA'],
                                                ['More than 50 users', '2× DIA'],
                                                ['5 to 50 users', '1 DIA + 1 Broadband'],
                                                ['Fewer than 5 users', '1 Broadband'],
                                            ].map(([c, v]) => (
                                                <TableRow key={c}>
                                                    <TableCell sx={{ p: 0.5, fontSize: 11 }}>{c}</TableCell>
                                                    <TableCell sx={{ p: 0.5, fontSize: 11 }}>{v}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </InfoTip>
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>{sf('connection_type', 'Connection Type', CONNECTION_TYPE_OPTIONS)}</Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControlLabel
                                control={<Switch checked={!!localData.circuit_already_ordered} onChange={handleChange} name="circuit_already_ordered" />}
                                label="Circuit Already Ordered?"
                            />
                        </Grid>
                        {localData.circuit_already_ordered && (
                            <Grid size={{ xs: 12, sm: 6 }}>{tf('circuit_demand_number', 'Circuit Demand Number', { required: true, helperText: 'Enter the demand number from the circuit order' })}</Grid>
                        )}

                        <Grid size={{ xs: 12 }}>
                            <Box sx={{ border: '1px solid #e0e7ef', borderRadius: 1, p: 2, bgcolor: '#f8f9fc', mt: 1 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#003781', mb: 0.25 }}>Bandwidth Estimation</Typography>
                                <Typography sx={{ fontSize: 12, color: '#777', mb: 1.5 }}>
                                    Fill the counts below to auto-calculate bandwidth (0.768 Mbps/user · 0.064 Mbps/phone ·
                                    2 Mbps/VC device + 10% buffer). You can also enter bandwidth directly if already known.
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, sm: 4 }}>{tf('num_users', 'Number of Users', { required: true, type: 'number', helperText: '0.768 Mbps per user' })}</Grid>
                                    <Grid size={{ xs: 12, sm: 4 }}>{tf('num_standard_phones', 'Number of Phones', { required: true, type: 'number', helperText: '0.064 Mbps per phone' })}</Grid>
                                    <Grid size={{ xs: 12, sm: 4 }}>{tf('num_vc_devices', 'Number of VC Devices', { required: true, type: 'number', helperText: '2 Mbps per device' })}</Grid>
                                    <Grid size={{ xs: 12, sm: 4 }}>
                                        {tf('bandwidth_mbps', 'Required Bandwidth (Mbps)', {
                                            type: 'number', required: true, onChange: onManualBw, min: calcBw || undefined,
                                            error: bwBelowMin ? bwMinMsg : undefined,
                                            helperText: manualBw ? 'Manually entered — counts above no longer override' : 'Auto-calculated (10% buffer) — edit to override',
                                        })}
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {/* ── LAN ────────────────────────────────────────────────────── */}
            {servicesOn.LAN && (
                <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                    <SectionLabel sx={{ fontSize: 16, fontWeight: 600 }}>Network Services Requirements — LAN</SectionLabel>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 4 }}>{tf('num_floors', 'Number of Floors', { required: true, type: 'number', placeholder: 'e.g. 3' })}</Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>{tf('lan_static_devices', 'Static Devices / Printers / Servers / Other Network Devices', { required: true, type: 'number', placeholder: 'e.g. 8', helperText: 'Fixed devices, excludes user endpoints' })}</Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>{tf('num_lan_comm_rooms', 'Communication Room(s)', { type: 'number', placeholder: 'e.g. 2' })}</Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>{sf('fe_required', 'Is Field Engineer Required for Onsite Activity?', YES_NO)}</Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>{tf('lan_users_per_floor', 'Number of Users per Floor', { required: true, type: 'number', placeholder: 'e.g. 30' })}</Grid>
                    </Grid>
                </Paper>
            )}

            {/* ── WiFi ───────────────────────────────────────────────────── */}
            {servicesOn.WiFi && (
                <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                    <SectionLabel sx={{ fontSize: 16, fontWeight: 600 }}>Network Services Requirements — WiFi</SectionLabel>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>{sf('wifi_survey_done', 'WiFi Survey Completed?', YES_NO, { required: true })}</Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>{tf('wifi_floor_dim_sqm', 'Floor Dimension (Square Meter)', { required: true, type: 'number', placeholder: 'e.g. 850' })}</Grid>
                    </Grid>

                    <SubDivider>Attachments</SubDivider>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <AttachmentUploader
                                kind="heatmap" label="Attach Heatmap"
                                attachments={attachments}
                                pendingFile={pendingAttachments.heatmap}
                                disabled={localData.wifi_survey_done !== 'Yes'}
                                uploading={!!attachmentUploading.heatmap}
                                uploadError={attachmentErrors.heatmap}
                                canDelete={isDraftState}
                                helperText="Enabled once WiFi Survey Completed? is Yes"
                                onUpload={(file) => handleAttachmentPick('heatmap', file)}
                                onDelete={(fileName) => handleAttachmentDelete(fileName, 'heatmap')}
                                onRemovePending={() => handleRemovePending('heatmap')}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <AttachmentUploader
                                kind="survey_result" label="Attach Survey Result"
                                attachments={attachments}
                                pendingFile={pendingAttachments.survey_result}
                                disabled={localData.wifi_survey_done !== 'Yes'}
                                uploading={!!attachmentUploading.survey_result}
                                uploadError={attachmentErrors.survey_result}
                                canDelete={isDraftState}
                                helperText="Enabled once WiFi Survey Completed? is Yes"
                                onUpload={(file) => handleAttachmentPick('survey_result', file)}
                                onDelete={(fileName) => handleAttachmentDelete(fileName, 'survey_result')}
                                onRemovePending={() => handleRemovePending('survey_result')}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <AttachmentUploader
                                kind="floor_plan" label="Attach Floor Plan" required
                                attachments={attachments}
                                pendingFile={pendingAttachments.floor_plan}
                                uploading={!!attachmentUploading.floor_plan}
                                uploadError={attachmentErrors.floor_plan}
                                canDelete={isDraftState}
                                helperText="Mandatory"
                                error={floorPlanError}
                                onUpload={(file) => handleAttachmentPick('floor_plan', file)}
                                onDelete={(fileName) => handleAttachmentDelete(fileName, 'floor_plan')}
                                onRemovePending={() => handleRemovePending('floor_plan')}
                            />
                        </Grid>
                    </Grid>

                    <Box sx={{ border: '1px dashed #b9c6dd', borderRadius: 1, p: 2, mb: 2.5, bgcolor: '#fbfcfe' }}>
                        <Typography variant="subtitle2" color="primary" sx={{ mb: 2 }}>Wi-Fi Heatmap — Site Information Required</Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>{tf('wifi_floors_in_scope', 'Floor in Scope', { placeholder: 'e.g. Floor 1, Floor 2, Ground Floor', helperText: 'Names/numbers, not a count — Number of Floors above covers the total' })}</Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>{tf('wifi_ceiling_height', 'Ceiling Height', { placeholder: 'e.g. 2.8m (3.5m in atrium)', helperText: 'Note whether heights vary within one floor' })}</Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>{tf('wifi_areas_in_scope', 'Area in Scope', { placeholder: 'e.g. Open office, meeting rooms, corridors' })}</Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>{tf('wifi_critical_areas', 'Critical Areas', { placeholder: 'e.g. Boardroom, Town Hall', helperText: 'VIP areas and high-density areas' })}</Grid>
                            <Grid size={{ xs: 12 }}>{tf('wifi_device_types_counts', 'Device Types & Counts', { multiline: true, rows: 2, placeholder: 'e.g. 40 laptops, 15 desk phones, 5 tablets (Floor 1)', helperText: 'Flag devices older than 10 years' })}</Grid>
                            <Grid size={{ xs: 12 }}>{tf('wifi_coverage_in_scope', 'Coverage in Scope', { multiline: true, rows: 2, placeholder: 'e.g. Open office, meeting rooms, corridors' })}</Grid>
                            <Grid size={{ xs: 12 }}>{tf('wifi_coverage_out_of_scope', 'Coverage out of Scope', { multiline: true, rows: 2, placeholder: 'e.g. Staircases, toilets, storage, elevators' })}</Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>{sf('wifi_data_usage_type', 'Data Usage Type', WIFI_DATA_USAGE_OPTIONS)}</Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>{tf('wifi_wall_types_pillars', 'Wall Types / Pillars', { placeholder: 'e.g. Concrete walls, glass partitions' })}</Grid>
                        </Grid>
                    </Box>

                    <SubDivider>Readiness</SubDivider>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 3 }}>{sf('site_ready_for_impl', 'Site Ready for Implementation?', YES_NO)}</Grid>
                        {localData.site_ready_for_impl === 'No' && (
                            <Grid size={{ xs: 12, sm: 3 }}>{tf('site_readiness_date', 'Date of Site Readiness', { type: 'date', helperText: 'Expected date the site will be ready for implementation' })}</Grid>
                        )}
                        <Grid size={{ xs: 12, sm: 3 }}>{sf('fe_required', 'Field Engineer Required?', YES_NO)}</Grid>
                        <Grid size={{ xs: 12, sm: 3 }}>{sf('floor_walk_done', 'Floor Walk Completed?', YES_NO, { helperText: 'Accenture reads this — leaving it unset reports No' })}</Grid>
                    </Grid>
                </Paper>
            )}

            {/* ── Voice ──────────────────────────────────────────────────── */}
            {servicesOn.Voice && (
                <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                    <SectionLabel sx={{ fontSize: 16, fontWeight: 600 }}>Network Services Requirements — Voice</SectionLabel>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 3 }}>{tf('num_premium_phones', 'Premium Phones', { type: 'number', placeholder: '0' })}</Grid>
                        <Grid size={{ xs: 12, sm: 3 }}>{tf('num_analogue_phones', 'Analogue Phones / Fax', { type: 'number', placeholder: '0' })}</Grid>
                        <Grid size={{ xs: 12, sm: 3 }}>{tf('num_vc_devices', 'VC Devices', { type: 'number', placeholder: '0' })}</Grid>
                        <Grid size={{ xs: 12, sm: 3 }}>{tf('num_conference_devices', 'Conference Devices', { type: 'number', placeholder: '1' })}</Grid>

                        <Grid size={{ xs: 12, sm: 4 }}>{sf('ddi_moved_or_procured', 'New DDI Required?', YES_NO, { required: true })}</Grid>
                        {localData.ddi_moved_or_procured === 'Yes' && (
                            <Grid size={{ xs: 12, sm: 4 }}>{sf('ddi_solution', 'Preferred DDI Solution', DDI_SOLUTION_OPTIONS, { helperText: 'SIP trunk / local GW if new DDI required' })}</Grid>
                        )}

                        <Grid size={{ xs: 12, sm: 6 }}>{tf('ddi_count', 'Count of DDIs (Moved / Procured)', { type: 'number', placeholder: '0' })}</Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>{tf('ddi_ported_count', 'Count of DDI to be Ported (non-AGN → AGN Env)', { type: 'number', placeholder: '0' })}</Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>{sf('ddi_shared_with_cc', 'DDI Shared with Call Center?', YES_NO)}</Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>{sf('ivr_arc_console_required', 'IVR / ARC Console Required?', YES_NO)}</Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>{sf('hunt_group_required', 'Hunt Group Required?', YES_NO)}</Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>{sf('pickup_group_required', 'Pickup Group Required?', YES_NO)}</Grid>
                    </Grid>
                </Paper>
            )}

            {/* ── Business Justification ────────────────────────────────── */}
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                <SectionLabel>Business Justification</SectionLabel>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>{tf('request_description', 'Description of ODM Request', { required: true, multiline: true, rows: 3, placeholder: 'Describe the ODM request in detail' })}</Grid>
                    <Grid size={{ xs: 12 }}>{tf('business_reasons', 'Business Reasons', { multiline: true, rows: 3, placeholder: 'Describe the business reasons for this new site' })}</Grid>
                </Grid>
            </Paper>

            {/* ── Cost Estimate (WAN, then LAN / WiFi / Voice in Services Required order) ── */}
            <Box sx={{ mb: 3 }}>
                {servicesOn.WAN && (
                    <Paper elevation={0} sx={{ p: 2.5, border: '1.5px solid #003781', borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                            <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#003781' }}>Initial Cost Estimate — New Site</Typography>
                            <Box sx={{ bgcolor: '#ed6c02', color: '#fff', fontSize: 11, fontWeight: 600, px: 1.25, py: 0.4, borderRadius: 5 }}>
                                Initial WAN Estimate — Final cost after Accenture WOP
                            </Box>
                        </Box>
                        <WanCostEstimateCard costEstimate={costEstimate} costEstimateLoading={costEstimateLoading} />
                    </Paper>
                )}
                {servicesOn.LAN && (
                    <MiniCostBox
                        tag="906758" title="LAN Port"
                        amount={(parseFloat(localData.num_lan_ports) || 0) * LAN_UNIT_PRICE}
                        desc={`Monthly recurring cost for LAN ports — ${localData.num_lan_ports || 0} ports (incl. 25% spare) × €${LAN_UNIT_PRICE.toFixed(2)}`}
                        note="Ports = Users + IP Phones + VC Devices + APs + Static/Server/Other Devices, plus 25% spare for future expansion."
                    />
                )}
                {servicesOn.WiFi && (
                    <MiniCostBox
                        tag="906799" title="WiFi Access Point"
                        amount={(parseFloat(localData.num_wifi_ap) || 0) * WIFI_UNIT_PRICE}
                        desc={`Monthly recurring cost for Wi-Fi Access Points — ${localData.num_wifi_ap || 0} APs × €${WIFI_UNIT_PRICE.toFixed(2)}`}
                        note="Estimated at high client density — 1 AP per 1,000–1,200 sq ft (~93–111 sq m)."
                    />
                )}
                {servicesOn.Voice && (
                    <MiniCostBox
                        tag="TBD" title="Voice Devices"
                        amount={0}
                        desc={`Voice devices in scope — ${voiceDeviceCount} device${voiceDeviceCount === 1 ? '' : 's'}`}
                    />
                )}
                <Alert severity="info" sx={{ mt: 1, fontSize: 12 }}>
                    Combined cost (New Site + Circuit Ordering) will be shown in the Review Quote popup after WOP submission. An Automation Input Template will be sent to Solutioning team  automatically on submission.
                </Alert>
            </Box>

            {/* ── Customer Contact ──────────────────────────────────────── */}
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                <SectionLabel>Customer Contact</SectionLabel>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>{tf('customer_name', 'Name', { required: true, helperText: 'Pre-filled from login — editable' })}</Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        {tf('customer_contact_mail', 'Email', {
                            required: true, helperText: 'Pre-filled from login — editable',
                            error: localData.customer_contact_mail ? validateEmail(localData.customer_contact_mail) : null,
                        })}
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        {tf('customer_contact_phone', 'Customer Contact Phone', {
                            required: true, error: localData.customer_contact_phone ? validatePhone(localData.customer_contact_phone) : null,
                            helperText: 'Format: +XX (XXX) XXX-XXXX',
                        })}
                    </Grid>

                    <Grid size={{ xs: 12, sm: 4 }}>{tf('site_technical_user_name', 'Technical Contact Name', { required: true, placeholder: 'e.g. Jane Smith' })}</Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        {tf('site_technical_user_email', 'Technical Contact Email', {
                            required: true, placeholder: 'e.g. jane.smith@allianz.com',
                            error: localData.site_technical_user_email ? validateEmail(localData.site_technical_user_email) : null,
                        })}
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        {tf('site_technical_user_phone', 'Technical Contact Number', {
                            required: true, error: localData.site_technical_user_phone ? validatePhone(localData.site_technical_user_phone) : null,
                            helperText: 'Format: +XX (XXX) XXX-XXXX',
                        })}
                    </Grid>
                </Grid>
            </Paper>

            {/* ── Actions ────────────────────────────────────────────────── */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveDraft}
                    disabled={isSubmitting || isSaving}
                    startIcon={isSaving ? <CircularProgress size={20} /> : null}
                >
                    {isSaving ? 'Saving...' : 'Save as Draft'}
                </Button>
                <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => navigate('/track-requests')}
                    disabled={isSubmitting || isSaving}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleFormSubmit}
                    disabled={isSubmitting || isSaving}
                    startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                </Button>
            </Box>

            <Dialog open={dialogOpen} onClose={handleDialogClose}>
                <DialogTitle>{dialogTitle}</DialogTitle>
                <DialogContent><Typography>{dialogMessage}</Typography></DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose}>OK</Button>
                </DialogActions>
            </Dialog>

            {/* Cost Estimate / Confirm Submit Dialog — same layout as Circuit Ordering */}
            <Dialog open={confirmSubmitOpen} onClose={() => setConfirmSubmitOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Typography variant="h6">
                        {costEstimate?.estimates?.length > 0 ? 'Cost Estimate — New Site' : 'Confirm Submission'}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }} gutterBottom>Selected Services</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        {Object.keys(servicesOn).filter(k => servicesOn[k]).map(k => (
                            <Box key={k} sx={{ bgcolor: '#f0f4ff', color: '#003781', fontSize: 12, fontWeight: 600, px: 1.25, py: 0.4, borderRadius: 5 }}>
                                {k}
                            </Box>
                        ))}
                    </Box>

                    {servicesOn.WAN && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>WAN</Typography>
                            {/* Mirrors the "Initial Cost Estimate — New Site" block on the form exactly. */}
                            <Paper elevation={0} sx={{ p: 2.5, border: '1.5px solid #003781', borderRadius: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#003781' }}>Initial Cost Estimate — New Site</Typography>
                                    <Box sx={{ bgcolor: '#ed6c02', color: '#fff', fontSize: 11, fontWeight: 600, px: 1.25, py: 0.4, borderRadius: 5 }}>
                                        Initial WAN Estimate — Final cost after Accenture WOP
                                    </Box>
                                </Box>
                                <WanCostEstimateCard costEstimate={costEstimate} costEstimateLoading={costEstimateLoading} />
                            </Paper>
                        </Box>
                    )}

                    {servicesOn.LAN && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>LAN</Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableBody>
                                        <TableRow>
                                            <TableCell>LAN Port (906758)</TableCell>
                                            <TableCell align="right">€{lanCost.toFixed(2)}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Typography variant="caption" sx={{ color: '#666', fontStyle: 'italic', display: 'block', mt: 0.5 }}>
                                {localData.num_lan_ports || 0} ports (incl. 25% spare) × €{LAN_UNIT_PRICE.toFixed(2)} — preliminary, confirmed after Accenture WOP.
                            </Typography>
                        </Box>
                    )}

                    {servicesOn.WiFi && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>WiFi</Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableBody>
                                        <TableRow>
                                            <TableCell>WiFi Access Point (906799)</TableCell>
                                            <TableCell align="right">€{wifiCost.toFixed(2)}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Typography variant="caption" sx={{ color: '#666', fontStyle: 'italic', display: 'block', mt: 0.5 }}>
                                {localData.num_wifi_ap || 0} APs × €{WIFI_UNIT_PRICE.toFixed(2)} — preliminary, confirmed after Accenture WOP.
                            </Typography>
                        </Box>
                    )}

                    {servicesOn.Voice && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>Voice</Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableBody>
                                        <TableRow>
                                            <TableCell>Voice Devices</TableCell>
                                            <TableCell align="right">€0.00</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Do you want to proceed with submitting this New Site Ordering request?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmSubmitOpen(false)} variant="outlined">Cancel</Button>
                    <Button onClick={handleConfirmedSubmit} variant="contained" sx={{ bgcolor: '#003781' }}
                        disabled={isSubmitting}>
                        {isSubmitting ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                        Approve &amp; Submit
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={successOpen} onClose={() => { setSuccessOpen(false); navigate('/track-requests'); }}>
                <DialogTitle sx={{ color: '#003781' }}>
                    <CheckCircleOutlineIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'success.main' }} />
                    Request Submitted Successfully
                </DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        Your New Site Ordering request has been submitted. The ODM Solution Engineering team and Accenture
                        will be notified automatically to prepare the Work Order Proposal (WOP).
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        You can track progress in <strong>Track Requests</strong>.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={() => { setSuccessOpen(false); navigate('/track-requests'); }} sx={{ bgcolor: '#003781' }}>
                        Go to Track Requests
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default NewSiteOrderingForm;
